import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  History,
  Loader2,
  RotateCcw,
  Server,
  TriangleAlert,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  workflowsApi,
  type WorkflowVersionRow,
} from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";

/**
 * Version history for a workflow, with restore.
 *
 * Exists because nodes/edges are overwritten wholesale on every save — by a
 * human on the canvas or by an agent calling update_workflow — and there was
 * previously no way back.
 *
 * Restoring is not destructive: the current state was itself snapshotted on
 * its way in, and the restore records a new version, so nothing is lost by
 * trying one. The confirm step exists because a restore replaces what's on
 * the canvas right now, which is startling if unintended — not because it's
 * irreversible.
 */
export function VersionHistoryDialog({
  workflowId,
  onClose,
  onRestored,
}: {
  workflowId: string;
  onClose: () => void;
  onRestored: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: versions, isLoading } = useQuery({
    queryKey: queryKeys.workflows.versions(workflowId),
    queryFn: () => workflowsApi.listVersions(workflowId),
  });

  const restore = useMutation({
    mutationFn: (versionId: string) =>
      workflowsApi.restoreVersion(workflowId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.detail(workflowId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.versions(workflowId),
      });
      onRestored();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not restore");
    },
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-[min(720px,92vw)] overflow-y-auto rounded-xl border border-border bg-background p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center gap-2">
          <History size={16} className="text-accent" />
          <h2 className="text-[15px] font-semibold">Version history</h2>
        </div>
        <p className="mb-4 text-xs text-text-muted">
          Every save is snapshotted — from the canvas and from agents.
          Restoring writes an old version back and records that as a new
          version, so nothing is lost either way.
        </p>

        {isLoading ? (
          <Loader2 size={18} className="animate-spin text-text-muted" />
        ) : !versions || versions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface/50 px-3 py-8 text-center">
            <p className="text-sm font-medium text-text-primary">
              No history yet
            </p>
            <p className="mt-1 text-xs text-text-muted">
              The next save will create the first version. History only covers
              changes made after this feature shipped.
            </p>
          </div>
        ) : (
          <ol className="space-y-2">
            {versions.map((v, i) => (
              <VersionRow
                key={v.id}
                version={v}
                isCurrent={i === 0}
                isConfirming={confirmId === v.id}
                isPending={restore.isPending && confirmId === v.id}
                onAskRestore={() => {
                  setError(null);
                  setConfirmId(v.id);
                }}
                onCancelRestore={() => setConfirmId(null)}
                onConfirmRestore={() => restore.mutate(v.id)}
              />
            ))}
          </ol>
        )}

        {error && (
          <p className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function VersionRow({
  version,
  isCurrent,
  isConfirming,
  isPending,
  onAskRestore,
  onCancelRestore,
  onConfirmRestore,
}: {
  version: WorkflowVersionRow;
  isCurrent: boolean;
  isConfirming: boolean;
  isPending: boolean;
  onAskRestore: () => void;
  onCancelRestore: () => void;
  onConfirmRestore: () => void;
}) {
  const author = authorPresentation(version);
  // Prompt loss is the destructive-rewrite signature this feature exists to
  // catch, so it gets visual weight rather than sitting in the summary text.
  const destructive = version.summary?.includes("cleared") ?? false;

  return (
    <li
      className={cn(
        "rounded-lg border bg-surface p-3",
        isCurrent ? "border-accent/40" : "border-border",
        destructive && "border-amber-500/40",
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
            author.tint,
          )}
          title={author.title}
        >
          <author.Icon size={12} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-[11px] text-text-muted">
              v{version.version}
            </span>
            <span className="truncate text-[13px] font-medium text-text-primary">
              {version.name}
            </span>
            {isCurrent && (
              <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-accent">
                current
              </span>
            )}
            {destructive && (
              <span className="flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
                <TriangleAlert size={9} />
                prompts lost
              </span>
            )}
          </div>

          <p className="mt-0.5 text-[11px] text-text-muted">
            {author.label} · {new Date(version.createdAt).toLocaleString()} ·{" "}
            {version.nodeCount} nodes, {version.edgeCount} edges
          </p>
          {version.summary && (
            <p className="mt-1 text-xs text-text-secondary">{version.summary}</p>
          )}
        </div>

        {!isCurrent && (
          <div className="shrink-0">
            {isConfirming ? (
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancelRestore}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={onConfirmRestore}
                  disabled={isPending}
                  className="gap-1.5"
                >
                  {isPending && <Loader2 size={11} className="animate-spin" />}
                  Confirm
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={onAskRestore}
                className="gap-1.5"
                title={`Replace the current graph with v${version.version}`}
              >
                <RotateCcw size={11} />
                Restore
              </Button>
            )}
          </div>
        )}
      </div>

      {isConfirming && (
        <p className="mt-2 rounded-md border border-border bg-background px-2.5 py-2 text-[11px] text-text-secondary">
          This replaces the graph on the canvas with v{version.version}. The
          current state is already saved as a version, so you can restore back.
        </p>
      )}
    </li>
  );
}

/** Icon, tint and label for who made a change. */
function authorPresentation(version: WorkflowVersionRow) {
  if (version.authorType === "agent") {
    return {
      Icon: Bot,
      tint: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
      title: "Changed by an agent",
      label: version.authorLabel
        ? `${version.authorLabel} (agent)`
        : "Agent",
    };
  }
  if (version.authorType === "system") {
    return {
      Icon: Server,
      tint: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400",
      title: "Recorded by the platform",
      label: version.authorLabel ?? "Platform",
    };
  }
  return {
    Icon: User,
    tint: "bg-accent/10 text-accent",
    title: "Changed by a person",
    label: version.authorLabel ?? "Canvas",
  };
}
